import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {useMemo, useState} from 'react';
import {ExamRequestResultTypeEnum} from 'domain/usecases/private/exam-results/get-exam-requests';
import {useExamReportsStore} from '../../store/useExamReportsStore';
import {useGetExamRequestReportsQuery} from '../../queries/useGetExamRequestReportsQuery';
import {useMedicalReportItem} from '../useMedicalReportItem';

const {downloadPartialResults, downloadCompleteResult, shareCompleteResult, sharePartialResults} =
	translations['pt-br'].privateFlow.examResults.drawer;

const {close} = translations['pt-br'].commons;

export const useExamRequestReports = (isWaitingResult: boolean) => {
	const {isMobile} = useMediaQueries();
	const examRequestData = useExamReportsStore(state => state.examRequestData);
	const closeDrawer = useExamReportsStore(state => state.resetExamRequestData);
	const examRequestId = examRequestData?.nuPedido?.toString() ?? '';
	const {
		data: examReports,
		isLoading,
		isError,
		refetch,
	} = useGetExamRequestReportsQuery(examRequestId);

	const hideShareButton =
		isWaitingResult ||
		(!isWaitingResult && examRequestData?.dsTipoExame !== ExamRequestResultTypeEnum.LABORATORY);

	const examReportsLength = examReports?.laudos?.length ?? 0;
	const showPlural = examReportsLength === 1 ? '' : 's';
	const [showSnackbar, setShowSnackbar] = useState(false);

	const medicalReport = useMemo(() => {
		const reports = examReports?.laudos;
		if (!reports?.length) return undefined;

		const finished = reports.filter(report => report.dtLaudo != null);
		if (!finished.length) return undefined;

		return finished[0];
	}, [examReports]);

	const {openPdf, operationState} = useMedicalReportItem({
		medicalReport,
		onLinkCopied: () => setShowSnackbar(true),
	});

	const handleCloseSnackbar = () => {
		setShowSnackbar(false);
	};

	const allReportsCompleted = examReports?.laudos?.every(exam => exam.dtLaudo != null);

	const isInfoAlert =
		operationState.error &&
		(operationState.errorType === 'notFound' || operationState.errorType === 'noContent');

	const isErrorAlert = operationState.error && operationState.errorType === 'generic';

	const downloadButtonLabel = allReportsCompleted
		? downloadCompleteResult
		: downloadPartialResults;
	const shareButtonLabel = allReportsCompleted ? shareCompleteResult : sharePartialResults;

	const shareButtonIsLoading = operationState.isShareAction && operationState.loading;

	const secondButtonLabel = isWaitingResult || hideShareButton ? close : downloadButtonLabel;
	const secondButtonAction = isWaitingResult || hideShareButton ? closeDrawer : () => openPdf();
	const secondButtonIsLoading =
		isWaitingResult || hideShareButton
			? false
			: !operationState.isShareAction && operationState.loading;

	const showContentAndActions = examReports && !isLoading && !isError;

	return {
		closeDrawer,
		examReports,
		examRequestData,
		isMobile,
		isError,
		isLoading,
		refetch,
		showPlural,
		openPdf,
		shareButtonLabel,
		shareButtonIsLoading,
		secondButtonLabel,
		secondButtonAction,
		secondButtonIsLoading,
		handleCloseSnackbar,
		isInfoAlert,
		isErrorAlert,
		showSnackbar,
		showContentAndActions,
		hideShareButton,
	};
};
